import { GridApi } from 'ag-grid-community';
import classNames from 'classnames';
import { union } from 'lodash';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { ProjectId, TaskIdV2 } from 'api/models';
import { FieldValueType } from 'api/tasks';
import { Column, SelectionsProps } from 'components/atoms/grid';
import { OnGridReady } from 'components/atoms/grid/plugins/use-on-grid-ready';
import { TableSkeleton } from 'components/atoms/table';
import { setMarkerForDataDog } from 'hooks/use-data-dog-logger';
import { CreatableEditableRefreshableContent, Loaded } from 'stores/core';
import { TaskField } from 'stores/fields';
import { Task } from 'stores/tasks';
import { queueMacrotask } from 'utils/macrotask';
import { GridChangeEvent } from '../grid-custom-column';
import { useTaskDrawer } from '../task-drawer';
import { BulkEditor } from '../tasks/bulk-editor';
import {
  Flex,
  LoadingOverlay,
  Overlay,
  OverlayContent,
  Wrapper,
} from '../tasks/components';
import { FilterFunction } from '../tasks/filter-functions';
import { PartialValue, TaskUpdateRequest } from '../tasks/use-update-tasks';
import { toRows } from '../tasks/utils';
import { Grid } from './grid';

const StyledGrid = styled(Grid)`
  .ag-row {
    border-color: var(--table-border-color);
  }
`;

interface BaseProps extends Omit<SelectionsProps, 'multiSelect'> {
  loading: boolean;
  filterFunction: FilterFunction | null;
  onCreate?: (expectations: PartialValue[]) => void;
  updateMultipleTasks: (requests: TaskUpdateRequest[]) => void;
  updateSingleTask: (requests: TaskUpdateRequest[]) => void;
  onDelete: (ids: TaskIdV2[]) => void;
  columns: Column[];
  fields: Record<number, TaskField>;
  provisionalRow?: Record<string, unknown>;
  noData?: ReactNode;
  noMatches?: ReactNode;
  ids?: any;
  handleGridChange?: (data: GridChangeEvent) => void;
}

const getTaskById = (
  isPaginationEnabled: boolean,
  gridApi: GridApi | undefined,
  lookup: any,
): ((id: any) => Task | null) => {
  return (id) => {
    const getTaskByIdFromLookup = (taskId: string): Task | null => {
      if (isPaginationEnabled) {
        return (gridApi?.getRowNode(String(id))?.data as Task) ?? null;
      }
      if (!lookup) {
        return null;
      }
      const taskWithStatus = lookup[taskId as unknown as number];

      if (!taskWithStatus) {
        return null;
      }

      const [status, task] = taskWithStatus;

      if (status === 'deleting' || status === 'empty') {
        return null;
      }
      if (!task) {
        return null;
      }

      return task;
    };

    return getTaskByIdFromLookup(id);
  };
};

interface PaginatedProp extends BaseProps {
  onReady: OnGridReady;
  isPaginationEnabled: boolean;
  gridApi?: GridApi;
  lookup?: any;
  ids?: never;
}
interface ClientSideRenderingProp extends BaseProps {
  lookup: Record<number, CreatableEditableRefreshableContent<Task>>;
  isPaginationEnabled?: any;
  onReady?: OnGridReady;
  gridApi?: GridApi;
  ids: number[];
}

type Props = PaginatedProp | ClientSideRenderingProp;

export const TaskGrid: FC<Props> = ({
  loading,
  columns,
  filterFunction,
  onCreate,
  updateMultipleTasks,
  updateSingleTask,
  onDelete,
  ids,
  lookup,
  provisionalRow,
  fields,
  noData: no_data,
  noMatches: no_matching_data,
  selected,
  onSelectionChange: onSelectedActual,
  selectableRef,
  onReady,
  isPaginationEnabled,
  gridApi,
  handleGridChange = () => {},
}) => {
  const { projectId: _projectId } = useParams<{ projectId: string }>();
  const onChange = useCallback(
    (
      { filtered, task, checkbox, ...record }: Record<string, unknown>,
      field: TaskField,
      from: FieldValueType,
      to: FieldValueType,
      callback?: VoidFunction,
    ) =>
      queueMacrotask(() => {
        if (record.taskId !== 'provisional') {
          updateSingleTask([
            { record, updates: [{ field, from, to }], callback },
          ]);

          return;
        }
        if (to == null) {
          return;
        }
        onCreate?.([{ field, to }]);
      }),
    [onCreate, updateSingleTask],
  );

  const [rows, setRows] = useState<Record<string, unknown>[]>();

  useEffect(() => {
    if (loading) {
      return;
    }
    if (isPaginationEnabled) {
      return;
    }
    if (!ids) {
      return;
    }
    setRows(toRows(ids, lookup));
  }, [loading, ids, lookup, isPaginationEnabled]);
  const show_message = !loading && provisionalRow == null;
  const show_no_data =
    show_message && no_data != null && (rows ?? []).length === 0;
  const show_no_matching_data =
    show_message &&
    no_matching_data != null &&
    filterFunction != null &&
    !(rows ?? []).some(filterFunction ?? Boolean);
  const [[taskDrawerState], show, hide] = useTaskDrawer();
  const multiSelectMode = selected.length > 0;

  const onSelected = useCallback(
    (selected_ids: string[]) => {
      if (selected_ids.length !== 0 && taskDrawerState === 'show') {
        hide();
      }
      onSelectedActual(selected_ids);
    },
    [onSelectedActual, taskDrawerState, hide],
  );

  const onTaskClick = useCallback(
    (id: TaskIdV2, projectId: ProjectId) => {
      show(projectId, id);
      const marker = _projectId
        ? 'project-list:task-load'
        : 'all-tasks:task-load';

      setMarkerForDataDog(marker);
    },
    [_projectId, show],
  );

  const projectIds = useMemo(
    () =>
      selected
        .map(getTaskById(isPaginationEnabled, gridApi, lookup))
        .filter(Boolean)
        .reduce(
          (project_ids, task) => union(project_ids, [task!.project]),
          [] as number[],
        )
        .map(ProjectId),
    [selected, isPaginationEnabled, gridApi, lookup],
  );

  const isPrivate = useMemo(
    () =>
      selected
        .map(getTaskById(isPaginationEnabled, gridApi, lookup))
        .filter(Boolean)
        .some((task) => task!.private),
    [selected, isPaginationEnabled, gridApi, lookup],
  );

  const getLookupForSelectedItem = useCallback(() => {
    if (!isPaginationEnabled) {
      return lookup;
    }

    const selectedLookup = selected
      .map(getTaskById(isPaginationEnabled, gridApi, lookup))
      .filter(Boolean)
      .map((task) => Loaded(task)) as Loaded<Task>[];

    return selectedLookup.reduce(
      (pre, [s, t]) => ({ ...pre, [t.taskId]: [s, t] }),
      {},
    );
  }, [gridApi, isPaginationEnabled, lookup, selected]);

  return (
    <Wrapper>
      <Flex
        className={classNames({
          'ag-theme-balham': true,
          'multi-select': multiSelectMode,
        })}
      >
        <StyledGrid
          multiSelect
          fields={fields}
          columns={columns}
          rows={rows}
          provisionalRow={provisionalRow}
          filter={
            filterFunction ? (record) => filterFunction(record) : undefined
          }
          selected={selected}
          selectableRef={selectableRef}
          onTaskClick={onTaskClick}
          onSelectionChange={onSelected}
          onChange={onChange}
          onGridReady={onReady}
          isPaginated={isPaginationEnabled}
          loading={loading}
          onColumnVisible={handleGridChange}
          onColumnMoved={handleGridChange}
          onNewColumnsLoaded={handleGridChange}
          onSortChanged={handleGridChange}
        />

        {loading ? (
          <LoadingOverlay>
            <TableSkeleton rowCount={24} columnCount={8} />
          </LoadingOverlay>
        ) : null}
      </Flex>
      <BulkEditor
        ids={selected}
        getLookup={getLookupForSelectedItem}
        onUpdate={updateMultipleTasks}
        onDelete={onDelete}
        onClose={() => {
          selectableRef?.current?.resetSelections?.();
        }}
        projectIds={projectIds}
        isPrivate={isPrivate}
      />
      {show_no_data || show_no_matching_data ? (
        <Overlay>
          <OverlayContent>
            {show_no_data ? no_data : no_matching_data}
          </OverlayContent>
        </Overlay>
      ) : null}
    </Wrapper>
  );
};
